import { createClient } from '@supabase/supabase-js';
import type { Database as TenantDatabase } from './types-tenant';

function normalizeSupabaseUrl(raw?: string): string {
  if (!raw) return '';
  let url = raw.trim();

  // Guard against accidentally including the key name in the value, e.g.
  // "VITE_SUPABASE_URL=https://..." or "SUPABASE_URL=https://..."
  url = url.replace(/^(?:VITE_)?SUPABASE_URL\s*=\s*/i, '');

  const isProjectRef = /^[a-z0-9]{20,}$/i.test(url);
  const isHostOnly = /^[a-z0-9\.-]+\.supabase\.co$/i.test(url);

  if (!/^https?:\/\//i.test(url)) {
    if (isHostOnly) {
      url = `https://${url}`;
    } else if (isProjectRef) {
      url = `https://${url}.supabase.co`;
    } else {
      console.warn('[Supabase] VITE_SUPABASE_URL missing protocol. Coercing:', url);
      url = `https://${url}`;
    }
  }

  url = url.replace(/\/+$/, '');
  return url;
}

const OVERRIDE_ALLOWED = !!import.meta.env.DEV;
const URL_OVERRIDE = OVERRIDE_ALLOWED && typeof window !== 'undefined' ? (localStorage.getItem('SUPABASE_URL_OVERRIDE') || undefined) : undefined;
const RAW_SUPABASE_URL = (URL_OVERRIDE || (import.meta.env.VITE_SUPABASE_URL as string | undefined));
const SUPABASE_URL = normalizeSupabaseUrl(RAW_SUPABASE_URL);

// Strip a mistaken "KEY " prefix if it was ever copied into the env value
const KEY_OVERRIDE = OVERRIDE_ALLOWED && typeof window !== 'undefined' ? (localStorage.getItem('SUPABASE_ANON_KEY_OVERRIDE') || undefined) : undefined;
const RAW_KEY = ((KEY_OVERRIDE || (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined))?.trim());
const SUPABASE_PUBLISHABLE_KEY = RAW_KEY?.replace(/^KEY\s+/i, '');

export const SUPABASE_OVERRIDE_ACTIVE = Boolean(URL_OVERRIDE || KEY_OVERRIDE);

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  if (import.meta.env.DEV) {
    console.warn('[Supabase] Missing env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local. Using dev fallbacks.');
  } else {
    throw new Error('Missing Supabase environment variables. Please check your .env file.');
  }
}

export const supabase = createClient<TenantDatabase>(SUPABASE_URL || 'https://invalid.local', SUPABASE_PUBLISHABLE_KEY || 'invalid');

export const isSupabaseReady = Boolean(SUPABASE_URL && SUPABASE_PUBLISHABLE_KEY && SUPABASE_URL !== '');

export const SUPABASE_URL_EFFECTIVE = SUPABASE_URL;
export const SUPABASE_RAW_URL = RAW_SUPABASE_URL;


if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log('[Supabase] Raw URL:', RAW_SUPABASE_URL, SUPABASE_OVERRIDE_ACTIVE ? '(override)' : '');
  console.log('[Supabase] Using URL:', SUPABASE_URL || 'https://invalid.local');
  console.log('[Supabase] Ready:', isSupabaseReady);
  console.log('[Supabase] Override active:', SUPABASE_OVERRIDE_ACTIVE);
}

